import type { TypeVideoConfig } from './types';

import { merge } from '../../helper';

import * as exModal from '../../components/modal';
import * as exElement from '../../utils/element';

// ----------------------------------------------------------------------------

function create(config: TypeVideoConfig = {}): HTMLVideoElement {
    // Default Config
    const defConfig: TypeVideoConfig = {
        fnError: true,
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    // Create element (video)
    const elVideo = exElement.create({
        merge: defConfig,
        tag: 'video',
    }) as HTMLVideoElement;

    // ------------------------------------------------------------------------

    // Settings
    settings(elVideo, defConfig);

    // Return
    return elVideo;
}

function settings(element: HTMLVideoElement, config: TypeVideoConfig = {}): void {
    /*
     * If using src
     * If src is string
     */
    if (typeof config.src === 'string') {
        element.src = config.src;
    }
    // If src is array
    else if (Array.isArray(config.src)) {
        // Foreach Source
        for (const item of config.src) {

            // Create element (source)
            const elSource = exElement.create({
                tag: 'source',
                attr: {
                    src: item,
                },
            });
            element.appendChild(elSource);
        }
    }

    // If using autoplay
    if (config.autoplay === true) {
        element.setAttribute('autoplay', '');
    }

    // If using poster
    if (config.poster) {
        element.setAttribute('poster', config.poster);
    }

    // If using controls
    if (config.controls === true) {
        element.setAttribute('controls', '');
    }

    // ------------------------------------------------------------------------

    // If using function loaded data
    if (typeof config.fnLoadedData === 'function') {
        const fnLoadedData = config.fnLoadedData;

        // Create event (loaded data)
        element.addEventListener('loadeddata', function (this: HTMLVideoElement, evt: Event): any {
            fnLoadedData.call(this, evt);
        });
    }

    // If using function loaded meta data
    if (typeof config.fnLoadedMetaData === 'function') {
        const fnLoadedMetaData = config.fnLoadedMetaData;

        // Create event (loaded meta data)
        element.addEventListener('loadedmetadata', function (this: HTMLVideoElement, evt: Event): any {
            fnLoadedMetaData.call(this, evt);
        });
    }

    // If using function error
    if (config.fnError instanceof Function || config.fnError === true) {
        // If using function error
        if (typeof config.fnError === 'function') {
            const fnError = config.fnError;

            // Create event (error)
            element.addEventListener('error', function (this: HTMLVideoElement, evt: Event): any {
                fnError.call(this, evt);
            });
        }
        // Is default
        else {
            // Create event (error)
            element.addEventListener('error', function (evt: Event): any {
                const video = evt.currentTarget as HTMLVideoElement;
                const error = video.error;

                if (error == null) {
                    console.error('An unknown error occurred.');
                    return;
                }

                // Switch Error
                switch (error.code) {
                    case error.MEDIA_ERR_ABORTED:
                        console.error('You aborted the video playback.');
                        break;
                    case error.MEDIA_ERR_NETWORK:
                        console.error('A network error caused the video download to fail part-way.');
                        break;
                    case error.MEDIA_ERR_DECODE:
                        console.error(
                            'The video playback was aborted due to a corruption problem or because the video used features your browser did not support.',
                        );
                        break;
                    case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
                        console.error(
                            'The video could not be loaded, either because the server or network failed or because the format is not supported.',
                        );
                        break;
                    default:
                        console.error('An unknown error occurred.');
                        break;
                }
            });
        }
    }

    // ------------------------------------------------------------------------

    // If using play
    if (config.play === true || !config.controls) {
        // Create element (click)
        element.addEventListener('click', function (): any {
            // If the video is not ready
            if (element.readyState == 0) {
                return;
            }

            // If the video paused or ended
            if (element.paused || element.ended) {
                void element.play();
            }
            // If the video played
            else {
                element.pause();
            }
        });
    }

    // If using modal
    if (config.modal) {
        // Create element (modal)
        const modal = exModal.create({
            merge: config.modal,
            backdrop: 'static',
            destroy: true,
        });
        modal.child(element).showModal();
    }
}

// ----------------------------------------------------------------------------

export { create };
